/**
 * PostHog — real-user performance measurement.
 *
 * Inert without a key, exactly like initSentry(): no token means no import, no
 * network, no cost. That is the state every environment is in until someone
 * sets VITE_POSTHOG_KEY in the Vercel dashboard, so it is the case that has to
 * behave perfectly.
 *
 * WHY A DYNAMIC IMPORT: posthog-js is ~57KB gzipped. A static import puts it in
 * the entry chunk for everyone, including the environments that never configure
 * it. Importing inside the guard means unconfigured builds pay nothing at all,
 * and configured ones load it off the critical path. (Session replay, surveys
 * and the web-vitals collector are lazy-loaded by posthog-js itself on top of
 * this.)
 *
 * WHY NO PostHogProvider: nothing calls usePostHog() yet, and the provider
 * needs the client eagerly — which would undo the lazy import above. Autocapture
 * tracks route changes through the History API, which BrowserRouter already
 * uses, so pageviews work without any React integration. Add @posthog/react the
 * day a component actually needs the hooks.
 *
 * IF A CSP IS EVER ADDED to this app (there is none today), it must allow
 * `https://*.posthog.com` in script-src and connect-src, plus `worker-src blob:`
 * for session replay. A missing directive fails SILENTLY — capture calls simply
 * never send, so the integration looks healthy while no data arrives.
 */
import log from '@/lib/logger'

let started = false

export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) {
    return
  }

  // main.tsx re-executes on HMR. posthog-js tolerates a second init(), but it
  // warns loudly, and guarding is cheaper than explaining the warning later.
  if (started) {
    return
  }
  started = true

  void import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, {
        // First-party path, proxied to PostHog by vercel.json rewrites.
        //
        // INCIDENT 2026-09-08: sending straight to eu.i.posthog.com meant every
        // request died with net::ERR_BLOCKED_BY_CLIENT for anyone running an ad
        // blocker — confirmed in production, zero events captured. Blockers
        // match on the destination DOMAIN, so routing through our own origin is
        // the only fix that works client-side.
        //
        // The path is deliberately meaningless: /analytics, /ingest, /track and
        // /posthog are all on public blocklists by name.
        api_host: import.meta.env.VITE_POSTHOG_HOST || '/agco',

        // Must be the REAL PostHog origin even when api_host is a proxy path,
        // or the toolbar and in-app links point at our own domain and 404.
        ui_host: 'https://eu.posthog.com',

        // Pinned config snapshot (batching, storage, rage-click tuning). Pinned
        // rather than floating so an SDK upgrade cannot silently change
        // behaviour; bump it deliberately.
        defaults: '2026-05-30',

        // Core Web Vitals are what this integration is FOR, and they are off
        // unless asked for. Passing the object forces them on from the client;
        // leaving it undefined defers to a project setting in the PostHog UI
        // that is easy to forget and invisible from the code.
        capture_performance: { web_vitals: true },

        // Heatmaps: clickmap (where they click), scrollmap (how far they read),
        // and the coordinate heatmap that also plots dead and rage clicks.
        // Set explicitly for the same reason as web vitals — the default defers
        // to a project setting nobody can see from the code. Heatmap data rides
        // along with existing events and is NOT separately billed.
        capture_heatmaps: true,

        // Scrollmap is built from pageview + pageleave. Without pageleave there
        // is no "how far down did they actually read" answer.
        capture_pageleave: true,

        // The two frustration signals, and the reason this integration exists.
        // A dead click is a click that changed nothing — someone expected that
        // thing to be clickable. A rage click is three clicks inside 30px and
        // one second — someone is stuck. Both default to true; pinned here so a
        // future defaults bump cannot quietly remove the metrics we report on.
        capture_dead_clicks: true,
        rageclick: true,

        // Deliberately OFF. Replay masks form inputs by default but NOT page
        // text, and these pages render client business data — names, revenue,
        // addresses. Turning it on needs a mask-everything-then-reveal policy
        // decided on purpose, not inherited from a default.
        disable_session_recording: true,
      })
    })
    .catch((error) => {
      // Measurement must never take the app down.
      log.warn('PostHog init skipped', { error: (error as Error)?.message })
      started = false
    })
}
